/**
 * Every <select> on a guest page opens a menu in the room's own colours on a computer, as the confirmed prototype
 * does (g-front, "dropdowns"): the browser's own list ignores the business's look (blue on Windows). The <select>
 * stays the source of truth, so every change handler keeps working; a phone or tablet keeps its own picker.
 */
import { useEffect } from "react";

interface Open {
  sel: HTMLSelectElement;
  menu: HTMLDivElement;
}

/** Puts the menus on every select under the page while it is shown. */
export function usePickMenus(): void {
  useEffect(() => installPickMenus(), []);
}

/** The listeners the prototype puts on the document; returns their removal. */
function installPickMenus(): () => void {
  let open: Open | null = null;
  const coarse = window.matchMedia?.("(pointer: coarse)");
  const usable = (sel: HTMLSelectElement | null): sel is HTMLSelectElement =>
    !!sel && !coarse?.matches && !sel.multiple && !(sel.size > 1) && !sel.disabled;
  const selectOf = (t: EventTarget | null) => (t instanceof Element ? t.closest("select") : null);

  const close = (focus = false) => {
    if (!open) return;
    const { sel, menu } = open;
    open = null;
    menu.remove();
    sel.classList.remove("pk-on");
    sel.setAttribute("aria-expanded", "false");
    if (focus && sel.isConnected) sel.focus({ preventScroll: true });
  };
  const choose = (sel: HTMLSelectElement, i: number) => {
    if (sel.selectedIndex === i) return;
    sel.selectedIndex = i;
    sel.dispatchEvent(new Event("input", { bubbles: true }));
    sel.dispatchEvent(new Event("change", { bubbles: true }));
  };
  const show = (sel: HTMLSelectElement) => {
    close();
    const menu = document.createElement("div");
    menu.className = "pk-menu";
    menu.setAttribute("role", "listbox");
    [...sel.options].forEach((o, i) => {
      const b = document.createElement("button");
      b.type = "button";
      b.className = i === sel.selectedIndex ? "pk-opt on" : "pk-opt";
      b.setAttribute("role", "option");
      b.setAttribute("aria-selected", String(i === sel.selectedIndex));
      b.textContent = o.textContent;
      b.disabled = o.disabled;
      b.dataset.i = String(i);
      menu.appendChild(b);
    });
    // Why inside the page, not the body: the menu reads the page's own colours from its root.
    (sel.closest(".gst") ?? document.body).appendChild(menu);
    const r = sel.getBoundingClientRect();
    menu.style.minWidth = `${Math.round(r.width)}px`;
    const h = menu.offsetHeight;
    const w = menu.offsetWidth;
    const below = r.bottom + 6 + h <= window.innerHeight - 8 || r.top - 6 - h < 8;
    menu.style.top = `${Math.round(below ? r.bottom + 6 : r.top - 6 - h)}px`;
    menu.style.left = `${Math.round(Math.max(8, Math.min(r.left, window.innerWidth - w - 8)))}px`;
    sel.classList.add("pk-on");
    sel.setAttribute("aria-expanded", "true");
    open = { sel, menu };
    (menu.querySelector<HTMLButtonElement>(".pk-opt.on") ?? menu.querySelector<HTMLButtonElement>(".pk-opt:not(:disabled)"))?.focus({
      preventScroll: true,
    });
  };

  const down = (e: MouseEvent) => {
    const sel = selectOf(e.target);
    if (usable(sel)) {
      e.preventDefault();
      if (open && open.sel === sel) close(true);
      else {
        sel.focus({ preventScroll: true });
        show(sel);
      }
      return;
    }
    if (open && !(e.target instanceof Node && open.menu.contains(e.target))) close();
  };
  const click = (e: MouseEvent) => {
    const b = e.target instanceof Element ? e.target.closest<HTMLButtonElement>(".pk-opt") : null;
    if (!b || !open) return;
    e.stopPropagation();
    const sel = open.sel;
    close(true);
    choose(sel, Number(b.dataset.i));
  };
  const key = (e: KeyboardEvent) => {
    if (open) {
      const opts = [...open.menu.querySelectorAll<HTMLButtonElement>(".pk-opt:not(:disabled)")];
      const i = opts.indexOf(document.activeElement as HTMLButtonElement);
      const to = (n: number) => {
        e.preventDefault();
        opts[n]?.focus({ preventScroll: true });
      };
      if (e.key === "ArrowDown") to((i + 1) % opts.length);
      else if (e.key === "ArrowUp") to((i - 1 + opts.length) % opts.length);
      else if (e.key === "Home") to(0);
      else if (e.key === "End") to(opts.length - 1);
      else if (e.key === "Escape" || e.key === "Tab") {
        e.preventDefault();
        e.stopPropagation();
        close(true);
      }
      return;
    }
    const sel = selectOf(e.target);
    if (usable(sel) && (e.key === "Enter" || e.key === " " || (e.altKey && e.key === "ArrowDown"))) {
      e.preventDefault();
      show(sel);
    }
  };
  const resize = () => close();
  const scroll = (e: Event) => {
    if (open && !(e.target instanceof Node && open.menu.contains(e.target))) close();
  };

  document.addEventListener("mousedown", down, true);
  document.addEventListener("click", click, true);
  document.addEventListener("keydown", key, true);
  window.addEventListener("resize", resize);
  window.addEventListener("scroll", scroll, true);
  return () => {
    close();
    document.removeEventListener("mousedown", down, true);
    document.removeEventListener("click", click, true);
    document.removeEventListener("keydown", key, true);
    window.removeEventListener("resize", resize);
    window.removeEventListener("scroll", scroll, true);
  };
}
